import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, FileText, Download, Upload, Cable, Wrench } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import type { SpecificationDocument } from '../../../types/index';
import { pipeSizes, Specifications } from '../../../mocks/Specification';
import { AddNewSpecification } from './AddNewSpecification';
import { PipelineSetup } from '../setup/components/PipelineSetup';
import { toast } from 'react-hot-toast';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { specificationsList } from '../../../mocks/specifications';
import { downloadSpecificationTemplate, processSpecificationUpload } from '../../../../../lib/specificationUtils';
import { PipeSize } from '../../../types/index';
import { PipelineSpecifications } from './PipelineSpecifications';
import { FabricationSpecifications } from './FabricationSpecifications';

interface ProjectSpecificationsProps {
  project: DashboardProject;
}

const SPEC_TYPE_LABELS = {
  'WPS': 'Welding Procedure Specification',
  'drawing': 'Drawing',
  'weld-process': 'Weld Process',
  'inspection-procedure': 'Inspection Procedure',
  'acceptance-criteria': 'Acceptance Criteria',
  'pass-config': 'Pass Configuration',
  'fitment-readings': 'Fitment Readings',
  'welding-position': 'Welding Position',
  'bevel': 'Bevel Details'
} as const;

export const ProjectSpecifications: React.FC<ProjectSpecificationsProps> = ({ project }) => {
  const [specifications, setSpecifications] = useState<SpecificationDocument[]>(Specifications);
  const [isAddingSpecification, setIsAddingSpecification] = useState(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);
  const [selectedSize, setSelectedSize] = useState<string>('0');
  const [specificationTab, setSpecificationTab] = useState<'pipeline' | 'fabrication'>(project.projectTypes.pipeline ? 'pipeline' : 'fabrication');
  const [pipeSize, setPipeSize] = useState<PipeSize[]>(pipeSizes);

  const handleAddSpecification = (specification: any) => {
    setSpecifications(prev => [...prev, specification]);
  };

  const handleImport = () => {
    // TODO: Implement import
  };

  const handleExport = () => {
    // TODO: Implement export
  };

  const handleDownload = (spec: SpecificationDocument) => {
    // TODO: Implement download
    console.log('Downloading specification:', spec);
  };

  const groupedSpecifications = useMemo(() => {
    const grouped = specifications.reduce((acc, spec) => {
      const pipeId = spec.pipeId;
      if (!acc[pipeId]) {
        acc[pipeId] = [];
      }
      acc[pipeId].push(spec);
      return acc;
    }, {} as Record<string, SpecificationDocument[]>);

    // Sort specifications within each pipe group by type and date
    Object.keys(grouped).forEach(pipeId => {
      grouped[pipeId].sort((a, b) => {
        if (a.type !== b.type) {
          return a.type.localeCompare(b.type);
        }
        return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
      });
    });

    return grouped;
  }, [specifications]);

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-6">
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold">Specifications</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  try {
                    downloadSpecificationTemplate();
                    toast.success('Template downloaded successfully');
                  } catch (error) {
                    console.error('Error downloading template:', error);
                    toast.error('Failed to download template');
                  }
                }}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              <Button
                onClick={() => document.getElementById('bulk-upload')?.click()}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Upload className="h-4 w-4" />
                Upload Specifications
                <input
                  id="bulk-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  aria-label="Upload specifications file"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      const loadingToast = toast.loading('Processing specifications...');
                      try {
                        const result = await processSpecificationUpload(e.target.files[0]);
                        console.log('Processed upload data:', result);
                        toast.success('Specifications uploaded successfully', {
                          id: loadingToast,
                        });
                      } catch (error) {
                        console.error('Error processing file:', error);
                        toast.error('Failed to process file. Please check the format and try again.', {
                          id: loadingToast,
                        });
                      }
                      e.target.value = '';
                    }
                  }}
                />
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <FileText className="h-4 w-4" />
                Export Report
              </Button>
              <Button
                onClick={() => setIsAddingSpecification(true)}
                size="sm"
                className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Plus className="h-4 w-4" />
                Add Specification
              </Button>
            </div>
          </div>



          {/* Specification Category Tabs */}
          <Tabs className="w-full"
            value={specificationTab}
            onValueChange={(value) => setSpecificationTab(value as 'pipeline' | 'fabrication')}
          >
            <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 rounded-t-lg">
              <TabsList className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-4 py-8">
                {project.projectTypes.pipeline && (
                  <TabsTrigger
                  className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                    value="pipeline"
                  >
                    <Cable className="h-4 w-4 mr-2" />Pipeline
                  </TabsTrigger>
                )}
                {project.projectTypes.fabrication && (
                  <TabsTrigger
                  className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                    value="fabrication"
                  >
                    <Wrench className="h-4 w-4 mr-2" />Fabrication
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {project.projectTypes.pipeline && (
              <TabsContent value="pipeline">
                <PipelineSpecifications
                  groupedSpecifications={groupedSpecifications}
                  pipeSizes={pipeSize}
                  handleDownload={handleDownload}
                />
              </TabsContent>
            )}

            {project.projectTypes.fabrication && (
              <TabsContent value="fabrication">
                <FabricationSpecifications
                  project={project}
                  groupedSpecifications={groupedSpecifications}
                  pipeSizes={pipeSize}
                  handleDownload={handleDownload}
                />
              </TabsContent>
            )}
          </Tabs>

          <AddNewSpecification
            isOpen={isAddingSpecification}
            onClose={() => setIsAddingSpecification(false)}
            onAdd={handleAddSpecification}
          />
        </>
      </CardContent>
    </Card>
  );
}; 